/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies, no-restricted-properties, no-unused-vars, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import EventStatus from '@linc-technologies/linced-common/enums/bookings/event-status';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import moment from 'moment';

const defaultSlotLength = 25;
const defaultSlotGap = 5;
const defaultStartTime = '15:30';
const defaultEndTime = '18:30';
const maxMaxBookings = 99;

export default class CreateController extends Controller {
  // Services
  @service
  router;

  @service
  session;

  @service
  store;

  @service
  strings;

  maxMaxBookings = maxMaxBookings;

  @tracked
  dateToAdd = moment().format('YYYY-MM-DD');

  @action
  setDateToAdd(value) {
    this.dateToAdd = value;
  }

  @tracked
  event = null;

  @tracked
  savingEvent = false;

  today = moment().format('YYYY-MM-DD');

  nextYear = moment().add(1, 'year').format('YYYY-MM-DD');

  @computed('event.dates.@each.date', 'dateToAdd')
  get dateValid() {
    let dateAlreadySet = this.event.dates.any((date) => {
      return date.date === this.dateToAdd;
    });
    if (dateAlreadySet) {
      return false;
    }
    return this.dateValidations.every((validation) => {
      return validation.validate(this.dateToAdd);
    });
  }

  dateValidations = Object.freeze([
    {
      message: 'Date is invalid',
      validate: (val) => {
        let parsed = moment(val);
        return parsed.isValid();
      },
    },
    {
      message: 'Date must be today or in the future',
      validate: (val) => {
        let parsed = moment(val);
        let now = moment();
        return parsed.isValid() && parsed.isSameOrAfter(now, 'd');
      },
    },
    {
      message: 'Date must be within 1 year',
      validate: (val) => {
        let parsed = moment(val);
        let nextYear = moment().add(1, 'year');
        return parsed.isValid() && parsed.isSameOrBefore(nextYear, 'd');
      },
    },
  ]);

  @tracked
  currentStep;

  @action
  setCurrentStep(value) {
    this.currentStep = value;
  }
  // sessionsValid means all start and end times for each session in each date are sequential
  @computed('event.{dates.@each.flattenedSessions,slotLength}')
  get sessionsValid() {
    let minDuration = this.event.slotLength * 60;
    return this.event.dates.every((date) => {
      // anything isn't used but forces ember to actually recompute this when date.flattenedSessions changes
      let anything = date.flattenedSessions;
      return date.sessions.every((session, index, sessions) => {
        if (session.endTime < session.startTime + minDuration) {
          // Session is too short, or ends before it starts.
          return false;
        }
        if (
          index > 0 &&
          session.startTime < sessions.objectAt(index - 1).endTime
        ) {
          // Session starts before the previous one ends.
          return false;
        }
        // Session times seem to be in order.
        return true;
      });
    });
  }

  @computed('event.{name,totalDates,slotLength,slotGap}', 'sessionsValid')
  get eventDefinitionValid() {
    return (
      this.event.get('name') !== '' &&
      this.event.get('totalDates') &&
      this.sessionsValid &&
      this.event.get('slotLength') > 0 &&
      this.event.get('slotLength') <= 60 &&
      this.event.get('slotGap') >= 0 &&
      this.event.get('slotGap') <= 30
    );
  }

  @computed('model.staff.[]', 'event.teacherGroupMappings.@each.teacherCount')
  get remainingTeachers() {
    let mappedTeachers = this.event.teacherGroupMappings.reduce(
      (teachers, mapping) => {
        teachers.push(...mapping.teachers);
        return teachers;
      },
      [],
    );
    return this.model.staff.filter((staff) => {
      return !mappedTeachers.some(function (teacher) {
        return staff.id === teacher.id;
      });
    });
  }

  @computed('model.groups.[]', 'event.teacherGroupMappings.@each.groupCount')
  get remainingGroups() {
    let mappedGroups = this.event.teacherGroupMappings.reduce(
      (groups, mapping) => {
        groups.push(...mapping.groups);
        return groups;
      },
      [],
    );
    return this.model.groups.filter((group) => {
      return !mappedGroups.some(function (mappedGroup) {
        return group.id === mappedGroup.id;
      });
    });
  }

  @computed(
    'event.teacherGroupMappings.@each.{teacherCount,groupCount,maxBookings}',
  )
  get teacherGroupMappingValid() {
    if (this.event.teacherGroupMappings.length === 0) {
      return false;
    }
    return this.event.teacherGroupMappings.every((mapping) => {
      if (
        mapping.teacherCount === 0 ||
        mapping.groupCount === 0 ||
        mapping.maxBookings < 1 ||
        mapping.maxBookings > this.maxMaxBookings
      ) {
        return false;
      }
      return true;
    });
  }

  @computed('event.teacherGroupMappings.@each.teacherCount')
  get teacherCount() {
    return this.event.teacherGroupMappings.reduce((count, mapping) => {
      return count + mapping.teacherCount;
    }, 0);
  }

  @computed('event.teacherGroupMappings.@each.groupCount')
  get groupCount() {
    return this.event.teacherGroupMappings.reduce((count, mapping) => {
      return count + mapping.groupCount;
    }, 0);
  }

  cleanupEvent() {
    // Remove event from store if we haven't saved it.
    if (this.event.id === null) {
      this.event.destroyRecord();
      this.event = null;
    }
  }

  setupEvent() {
    this.currentStep = 0;
    if (this.model.event) {
      this.event = this.model.event;
    } else {
      this.event = this.store.createRecord('bookings/event', {
        schoolId: this.session.school,
        name: '',
        slotLength: defaultSlotLength,
        slotGap: defaultSlotGap,
        // Plain arrays, not A(): dates/teacherGroupMappings are
        // @fragmentArray attributes - Ember Data always wraps whatever
        // they're set to in a real FragmentArray, so the initializer's own
        // type doesn't matter (verified against the installed
        // ember-data-model-fragments source earlier in this migration).
        // The field's own consumers (createFragment/removeFragment below)
        // operate on that materialized value, not this default.
        dates: [],
        teacherGroupMappings: [],
        staffBookingManagement: false,
        consecutiveBookings: true,
        consecutiveBookingMessage: '',
        status: EventStatus.DRAFT,
      });
    }
  }

  @action
  addDate() {
    this.event.dates.createFragment({
      date: this.dateToAdd,
      sessions: [
        {
          startTime: moment(
            `${this.dateToAdd} ${defaultStartTime}`,
            'YYYY-MM-DD HH:mm',
          ).unix(),
          endTime: moment(
            `${this.dateToAdd} ${defaultEndTime}`,
            'YYYY-MM-DD HH:mm',
          ).unix(),
        },
      ],
    });
    this.event.set(
      'dates',
      this.event.dates
        .slice()
        .sort((a, b) => (a.date || '').localeCompare(b.date || '')),
    );
  }

  @action
  deleteDate(date) {
    this.event.dates.removeFragment(date);
  }

  // Maps any defined homerooms where the teacher(s) & the group have not already been mapped
  // Also removes the default empty mapping if it's there still (to tidy up the typical use case)
  @action
  mapClasses() {
    this.set(
      'event.teacherGroupMappings',
      this.event.teacherGroupMappings.filter((mapping) => {
        return mapping.teacherIds.length + mapping.groupIds.length;
      }),
    );
    let classes = this.model.groups.filter(
      (item) => item.type === GroupType.HOMEROOM,
    );
    classes.forEach((currentClass) => {
      let teachers = currentClass.currentStaffPeople.filter((teacher) => {
        return this.remainingTeachers.includes(teacher);
      });
      if (teachers.length && this.remainingGroups.includes(currentClass)) {
        this.event.teacherGroupMappings.createFragment({
          teacherIds: teachers.map((item) => item.id),
          groupIds: [currentClass.id],
          maxBookings: 1,
        });
      }
    });
  }

  @action
  addTeacher(mapping, teacher) {
    mapping.teacherIds.pushObject(teacher.id);
  }

  @action
  removeTeacher(mapping, teacher) {
    mapping.teacherIds.removeObject(teacher.id);
  }

  @action
  addGroup(mapping, group) {
    mapping.groupIds.pushObject(group.id);
  }

  @action
  removeGroup(mapping, group) {
    mapping.groupIds.removeObject(group.id);
  }

  @action
  addTeacherGroupMapping() {
    this.event.teacherGroupMappings.createFragment({
      teacherIds: [],
      groupIds: [],
      maxBookings: 1,
    });
  }

  @action
  deleteTeacherGroupMapping(mapping) {
    this.event.teacherGroupMappings.removeFragment(mapping);
  }

  @action
  toggleConsecutiveBookings() {
    this.event.toggleProperty('consecutiveBookings');
  }

  @action
  createEvent() {
    // Guards against a double-click firing two concurrent saves (and
    // creating a duplicate event).
    if (this.savingEvent) {
      return;
    }
    this.savingEvent = true;
    this.event
      .save()
      .then(() => {
        this.router.transitionTo('admin.bookings', {
          queryParams: { statusFilter: EventStatus.DRAFT },
        });
      })
      .catch(() => {
        this.toaster.error('Error saving Event!');
      })
      .finally(() => {
        this.savingEvent = false;
      });
  }
}
